import React, {useEffect, useMemo} from 'react';
import DesignSystemTrackingProvider from 'design-system/src/Provider/TrackingProvider/TrackingProvider';
import {
  TrackingProvider as AppEventTrackingProvider,
  useTrackingContext,
  type TrackingAdapter,
} from '@src/Providers/Tracking/TrackingContext';
import {createTealiumAdapter} from '@src/Providers/Tracking/adapters/tealiumAdapter';
import {createAmplitudeAdapter} from '@src/Tracking/adapters/amplitudeAdapter';
import {createBrazeAdapter} from '@src/Tracking/adapters/brazeAdapter';
import {createAppsFlyerAdapter} from '@src/Tracking/adapters/appsflyerAdapter';
import {createBackendRelayAdapter} from '@src/Tracking/adapters/backendRelayAdapter';
import {createConsoleAdapter} from '@src/Tracking/adapters/consoleAdapter';
import useTealiumContext from '@src/Services/Tealium/hooks/useTealiumContext';
import {initTealium} from '@src/Services/Tealium/tealiumInit';
import {isTealiumEnabled} from '@src/Services/Tealium/isTealiumEnabled';
import apiConfig from '@src/Configs/apiConfig';
import {TraceTokEvents} from '@src/Tracking/catalog';
import useGlobalErrorTracking from '@src/Tracking/useGlobalErrorTracking';

/**
 * Emits the canonical cta_clicked event for design-system button presses.
 */
const InnerDsTracking: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {emit} = useTrackingContext();
  useGlobalErrorTracking();
  return (
    <DesignSystemTrackingProvider
      events={{
        onButtonPress: ({title}) => {
          // cta_clicked canonical
          emit(TraceTokEvents.CTAClicked, {
            cta_id: title,
            cta_name: title,
            cta_type: 'button',
          });
        },
      }}>
      {children as any}
    </DesignSystemTrackingProvider>
  );
};

const AppTrackingProvider = (props: {children: React.ReactNode}) => {
  const {children} = props;
  useTealiumContext();

  const adapters = useMemo<TrackingAdapter[]>(() => {
    const list: TrackingAdapter[] = [
      createTealiumAdapter(),
      createAmplitudeAdapter(),
      createBrazeAdapter(),
      createBackendRelayAdapter(),
    ];
    if (apiConfig.tracking.appsFlyerWebEnabled) {
      list.push(createAppsFlyerAdapter());
    }
    if (apiConfig.tracking.consoleAdapterEnabled) {
      list.push(createConsoleAdapter());
    }
    return list;
  }, []);

  useEffect(() => {
    if (!isTealiumEnabled()) return;
    initTealium().catch(err => {
      console.warn('[tealium] init failed', err);
    });
  }, []);

  return (
    <AppEventTrackingProvider enabled adapters={adapters}>
      <InnerDsTracking>{children}</InnerDsTracking>
    </AppEventTrackingProvider>
  );
};

export {TraceTokEvents};
export default AppTrackingProvider;
